import { forwardRef, useMemo } from "react";
import { useTheme } from "styled-components";
import { Languages } from "waapi/front-utils/hooks/useTranslation/types.js";
import { SelectProps } from "waapi/waap-ui/components/Select/Select.types.js";
import { SelectV2, SelectV2Components } from "waapi/waap-ui/components/SelectV2/SelectV2.js";
import { Text } from "waapi/waap-ui/components/Text/Text.js";
import { useMystiqueTranslation } from "../../../customHooks/useMystiqueTranslation/index.js";
import { useUpdateLanguage } from "../../../entities/UserInfos/hooks/useUpdateLanguage/index.js";
import { changeAppLanguage } from "../../../utils/changeAppLanguage.js";
import { useExtensionInfos } from "../../../zustand/extensionInfos.js";
import { OLD_I18N_STORAGE_KEY } from "../App/ExtensionChecks/constants.js";
import { extendedLabel, flagMatch } from "./constants.js";
import { StyledLangOptionLabel } from "./style.js";
import { LangOptionLabelProps, LanguageSelectProps } from "./types.js";

const LangOptionLabel = ({ value, size, onlyFlag }: LangOptionLabelProps): JSX.Element => {
    return (
        <StyledLangOptionLabel align="center" size={size} gap="8">
            {flagMatch[value]}
            {!onlyFlag && size !== "small" && <Text>{extendedLabel[value]}</Text>}
        </StyledLangOptionLabel>
    );
};

export const LanguageSelect = forwardRef<SelectProps, LanguageSelectProps>(({ size = "large", onlyFlag, handleChange, isFloating }, _ref) => {
    const { changeLanguage, currentLanguage, languages } = useMystiqueTranslation();

    const theme = useTheme();

    const extensionId = useExtensionInfos((state) => state.extensionId);
    const { mutate: updateLanguage } = useUpdateLanguage();

    const onChangeLanguage = (newLanguage: Languages | undefined) => {
        if (!newLanguage) return;

        if (handleChange) {
            handleChange(newLanguage);
            return;
        }

        changeAppLanguage({
            newLanguage,
            changeI18nLanguage: changeLanguage,
            extensionId,
            currentLanguage,
            persistToBackend: updateLanguage,
        });

        const oldLanguage = localStorage.getItem(OLD_I18N_STORAGE_KEY);
        if (oldLanguage) localStorage.setItem(OLD_I18N_STORAGE_KEY, newLanguage);
    };

    const options = useMemo(() => {
        return languages.map((ln) => {
            return {
                value: ln,
                label: "",
                render: (props) => {
                    return (
                        <SelectV2Components.Option {...props}>
                            <LangOptionLabel onlyFlag={onlyFlag} value={props.value} size={size} />
                        </SelectV2Components.Option>
                    );
                },
            };
        });
    }, [languages, onlyFlag, size]);

    const getWidth = () => {
        if (isFloating) return "fit-content";
        if (onlyFlag) return "7rem";
        return "100%";
    };

    return (
        <SelectV2
            prependElements={<LangOptionLabel onlyFlag={onlyFlag} value={currentLanguage} size={size} />}
            size={size}
            minWidth={theme.spacing[60]}
            placeholder={" "}
            width={getWidth()}
            options={options}
            value={currentLanguage}
            searchable={false}
            onChange={onChangeLanguage}
        />
    );
});
